/* eslint-disable camelcase, no-console */
import { analyticsEnabled, BLUR_TIMEOUT } from '../../config';
import { makeQueuedRequest } from './Request';
import { createOrGetVisitor } from './Visitor';
import {
  disableAnalytics,
  getDomainTag,
  getReferrer,
  getReferrerVal,
  // eslint-disable-next-line no-unused-vars
  getVisitorId,
  getCompanyId,
  getProduct
} from './Amalgam';
import {
  cancelAnalytics as cancelApiAnalytics,
  firebaseAnalyticsOpenEvent,
  firebaseAnalyticsOpenEventViewTime,
  firebaseAnalyticsProductSelectedEvent,
  firebaseAnalyticsProductUnselectedEvent,
  firebaseAnalyticsViewEvent,
  firebaseAnalyticsViewEventMore,
  openCatalogue,
  openProduct,
  openTag,
  startViewTimer,
  viewCatalogue,
  viewProduct
} from './Api';
import { getShowcaseId, getShowcaseSlug } from './Showcase';
import {
  getDatabaseRef,
  getSecondaryDatabaseRef,
  getServerTimestamp,
  onDatabaseValue,
  pushDatabaseRef,
  setDatabaseRef,
  updateDatabaseRef,
  onDatabaseDisconnect,
  offDatabase
} from './Firebase';
import { getCatalogueId } from './Catalogue';
import EventBus from './EventBus';
import { isWindowInFocus } from './Dom';
import { isSearchResultView } from './Utils';
import { catalogueViewed, productViewed } from './Socket';
import {
  disableAnalytics as experimentDisableAnalytics,
  getFirebaseAnalyticsViewTimeInterval,
  useQueueForFirebaseAnalytics
} from './Experiments';

// this will be deprecated in favour of analytics v2 in the future
let focusListenerInitialized = false;
let initialized = false;
let scrollTimeout = null;
const socketInterval = 1000;
let viewing_now_ref_object = {};
let _viewing_now_product_promise = null;
let _viewingNowSocketProductViewInterval = null;
let _viewing_now_promise;
let _viewingNowSocketCatalogueViewInterval = null;
let _inFocus = false;
let _open_id;
let _visitor_id = '';
let _visitor;
let viewingSince = 0;
let viewing_now_event_ref;
let pausedInterval = false;
let viewingNowProductEventTimerRef = null;
let isTimeoutInProgress = false;
let onlineShowcaseVisitorConnection = null;
let onlineCatalogueVisitorConnection = null;
let currentAnalyticsSlug = null;
let focusListenersSubscriber = null;
let connectedRef = null;
let primaryConnectedRef = null;
let primaryOnlineShowcaseVisitorConnection = null;
let primaryOnlineCatalogueVisitorConnection = null;

const isAnalyticsEnabled = () => {
  if (isSearchResultView()) {
    return false;
  }

  return analyticsEnabled && !disableAnalytics() && !experimentDisableAnalytics();
};

export const getOpenId = () => _open_id;

const getEventRef = ({ showcaseId } = {}) => {
  const ref = getDatabaseRef(`events/${showcaseId || getShowcaseId()}`);
  return pushDatabaseRef(ref);
};

const getBasicEventObject = (id, event_type, domainTag, { showcaseId, catalogueId } = {}) => {
  const obj = {
    id,
    showcaseId: showcaseId || getShowcaseId(),
    catalogueId: catalogueId || getCatalogueId(),
    visitorId: _visitor_id,
    timestamp_created: getServerTimestamp(),
    event_type,
    open_event_id: _open_id || null
  };

  if (domainTag) {
    obj.domainTag = domainTag;
  }

  return obj;
};

const setShowcaseVisitorEvent = (event) => {
  // Note: removed this on purpose, don't want to send showcase-visitor-events data to firebase
  // const ref = getDatabaseRef(
  //   `showcase-visitor-events/${event.showcaseId}/${event.visitorId || getVisitorId()}/${event.id}`
  // );
  // setDatabaseRef(ref, event).catch(() => {});
};

const setShowcaseVisitorEventMore = ({ visitorId, id }, more, { showcaseId } = {}) => {
  // Note: removed this on purpose, don't want to send showcase-visitor-events data to firebase
  // const ref = getDatabaseRef(
  //   `showcase-visitor-events/${showcaseId || getShowcaseId()}/${visitorId}/${id}/detail`
  // );
  // setDatabaseRef(ref, more).catch(() => {});
};

const openEventQueue = (visitor, { showcaseSlug } = {}) => {
  if (!_open_id) {
    const eventRef = getEventRef();
    const openId = eventRef.key;
    _open_id = openId;

    const domainTag = getDomainTag();

    firebaseAnalyticsOpenEvent({
      openId,
      domainTag,
      showcaseId: getShowcaseId(),
      catalogueId: getCatalogueId(),
      visitorId: _visitor_id,
      referrerType: getReferrer() || null,
      referrerValue: getReferrerVal() || null
    })
      .then(() => {
        openCatalogue({ showcaseSlug, openId });

        makeQueuedRequest({
          method: 'POST',
          url: '/catalogueTimestamp',
          params: { showcaseSlug: getShowcaseSlug() }
        });
      })
      .catch(() => {});
  }
};

const openEvent = (visitor, { showcaseSlug } = {}) => {
  if (!isAnalyticsEnabled()) {
    return;
  }

  if (useQueueForFirebaseAnalytics()) {
    return openEventQueue(visitor, { showcaseSlug });
  }

  if (!_open_id) {
    const eventRef = getEventRef();
    const openId = eventRef.key;
    _open_id = openId;

    const domainTag = getDomainTag();
    const showcaseId = getShowcaseId();
    const showcaseSlug = getShowcaseSlug();
    const timestampCreated = Date.now();

    const eventObj = {
      last_open_id: openId,
      last_open_timestamp: timestampCreated
    };

    eventObj[openId] = {
      timestamp_created: timestampCreated,
      read: false
    };

    if (domainTag) {
      eventObj[openId].domainTag = domainTag;
    } else if (getReferrer()) {
      eventObj[openId].domainTag = {
        referrerType: getReferrer(),
        referrerValue: getReferrerVal() || null
      };
    }

    Promise.all([
      updateDatabaseRef(
        getDatabaseRef(`showcase-visitor-open/${showcaseId}/${visitor.id}`),
        eventObj
      ),
      setDatabaseRef(
        getDatabaseRef(`showcase-visitor-open/${showcaseId}/last_visitor_timestamp/`),
        timestampCreated
      )
    ])
      .then(() => {
        openCatalogue({ showcaseSlug, openId });

        makeQueuedRequest({
          method: 'POST',
          url: '/catalogueTimestamp',
          params: {
            showcaseSlug,
            timestamp: timestampCreated
          }
        });
      })
      .catch(() => {});

    setShowcaseVisitorEvent(getBasicEventObject(eventRef.key, 'opened', domainTag));
  }
};

export const cancelPreviousProductViewingNowInterval = (status, newProductId, newImageId) => {
  try {
    if (!isAnalyticsEnabled()) {
      return;
    }

    const currentProductID = viewing_now_ref_object.productId;
    const currentImageId = viewing_now_ref_object.imageId;
    const refObj = _viewing_now_product_promise;
    const socketRefObj = _viewingNowSocketProductViewInterval;

    if (
      !newProductId ||
      ((refObj || socketRefObj || viewingNowProductEventTimerRef) &&
        (currentProductID !== newProductId || (newImageId && currentImageId !== newImageId)))
    ) {
      isTimeoutInProgress = false;
      if (refObj) {
        clearInterval(refObj);
        _viewing_now_product_promise = null;
      }
      if (socketRefObj) {
        clearInterval(socketRefObj);
        _viewingNowSocketProductViewInterval = null;
      }
      if (viewingNowProductEventTimerRef) {
        clearTimeout(viewingNowProductEventTimerRef);
        viewingNowProductEventTimerRef = null;
      }
      if (viewing_now_ref_object.status === 'started') {
        viewing_now_ref_object.status = status;
      }
    }
  } catch (err) {
    // Handle error
  }
};

const cancelPreviousViewingNowInterval = () => {
  if (!isAnalyticsEnabled()) {
    return;
  }

  if (_viewing_now_promise) {
    clearInterval(_viewing_now_promise);
  }

  if (_viewingNowSocketCatalogueViewInterval) {
    clearInterval(_viewingNowSocketCatalogueViewInterval);
  }
};

const windowblur = () => {
  if (!isAnalyticsEnabled()) {
    return;
  }
  _inFocus = false;
  cancelPreviousProductViewingNowInterval('paused', null);
  cancelPreviousViewingNowInterval();
};

export const viewingNowProductEvent = (productId, imageId) => {
  try {
    const showcaseSlug = getShowcaseSlug();
    const showcaseId = getShowcaseId();

    createOrGetVisitor()
      .then((visitor) => {
        if (
          viewing_now_ref_object.productId === productId &&
          viewing_now_ref_object.imageId === imageId &&
          viewing_now_ref_object.status === 'started'
        ) {
          return;
        }

        if (!isAnalyticsEnabled()) {
          return;
        }

        if (!_open_id) {
          openEvent(visitor, { showcaseSlug });
        }
        if (
          ((viewing_now_ref_object.productId !== productId ||
            viewing_now_ref_object.imageId !== imageId) &&
            (viewing_now_ref_object.status === 'started' ||
              viewing_now_ref_object.status === 'paused')) ||
          isTimeoutInProgress
        ) {
          cancelPreviousProductViewingNowInterval('finished', productId, imageId);
        }

        let viewingSince = 1;
        const eventRef = getEventRef();
        const viewing_now_id = eventRef.key;
        const viewing_now_obj = getBasicEventObject(viewing_now_id, 'viewing_now_product', null);

        const _vObj = {
          productId,
          imageId,
          eventObj: viewing_now_obj,
          status: 'started'
        };

        const more = {
          viewing_since: viewingSince,
          latest_timestamp: getServerTimestamp(),
          productId,
          imageId
        };

        viewing_now_obj.detail = more;

        viewing_now_ref_object = _vObj;

        if (!_inFocus) {
          return;
        }

        if (viewingNowProductEventTimerRef) {
          clearTimeout(viewingNowProductEventTimerRef);
          viewingNowProductEventTimerRef = null;
        }

        if (
          productId === 'catalogue-description' ||
          productId === 'CATALOGUE-DESCRIPTION' ||
          productId === 'CATALOGUE-INFO' ||
          productId === 'HOW-TO-ORDER' ||
          productId === 'HOME-FOOTER'
        ) {
          return;
        }

        openProduct({
          productId,
          imageId,
          eventId: viewing_now_id
        });

        viewProduct(viewingSince, productId, imageId, viewing_now_id, { showcaseSlug });

        viewingNowProductEventTimerRef = setTimeout(() => {
          isTimeoutInProgress = true;

          if (useQueueForFirebaseAnalytics()) {
            firebaseAnalyticsViewEvent({
              eventId: viewing_now_id,
              showcaseId: getShowcaseId(),
              catalogueId: getCatalogueId(),
              visitorId: _visitor_id,
              openId: _open_id || null,
              viewingSince,
              productId,
              imageId
            }).catch((err) => {
              console.log('ERROR in firebaseAnalyticsViewEvent', err);
            });
          } else {
            setShowcaseVisitorEvent(viewing_now_obj);
          }

          if (viewing_now_ref_object.productId !== productId) {
            return;
          }

          _viewingNowSocketProductViewInterval = setInterval(() => {
            if (pausedInterval) {
              return;
            }
            viewingSince += 1;
            viewProduct(viewingSince, productId, imageId, viewing_now_id, { showcaseSlug });

            productViewed(
              productId,
              getCompanyId(),
              getCatalogueId(),
              _visitor_id,
              _open_id,
              viewing_now_id,
              getProduct(productId).pictureUrl
            );
          }, socketInterval);

          _viewing_now_product_promise = setInterval(() => {
            if (pausedInterval) {
              return;
            }
            viewingSince += getFirebaseAnalyticsViewTimeInterval();
            viewProduct(viewingSince, productId, imageId, viewing_now_id, { showcaseSlug });

            const more = {
              viewing_since: viewingSince,
              latest_timestamp: getServerTimestamp(),
              productId,
              imageId
            };

            viewing_now_obj.detail = more;

            if (useQueueForFirebaseAnalytics()) {
              firebaseAnalyticsViewEventMore({
                eventId: viewing_now_id,
                showcaseId,
                visitorId: _visitor_id,
                viewingSince,
                productId,
                imageId,
                catalogueId: getCatalogueId(),
                openId: _open_id
              }).catch((err) => {
                console.log('ERROR in firebaseAnalyticsViewEventMore', err);
              });
            } else {
              setShowcaseVisitorEventMore(viewing_now_obj, more, { showcaseId });
            }
          }, getFirebaseAnalyticsViewTimeInterval() * 1000);
        }, 1000);
      })
      .catch(() => {});
  } catch (err) {
    // Handle error
  }
};

const viewingNowEvent = (visitor, { showcaseId, showcaseSlug }) => {
  if (!isAnalyticsEnabled()) {
    return;
  }

  if (!_open_id) {
    openEvent(visitor, { showcaseSlug });
  }

  startViewTimer(_open_id, (err, result) => {
    if (err) {
      return;
    }

    if (result && result.isBlocked) {
      window.location.href = '/s/error';
    }
  });

  if (!viewing_now_event_ref) {
    viewing_now_event_ref = getEventRef();
  }

  const eventRef = viewing_now_event_ref;

  const viewing_now_id = eventRef.key;
  const viewing_now_obj = getBasicEventObject(viewing_now_id, 'viewing_now', null);

  viewCatalogue(viewingSince, { showcaseSlug });

  viewing_now_obj.detail = {
    viewing_since: viewingSince,
    latest_timestamp: getServerTimestamp()
  };

  cancelPreviousViewingNowInterval();

  _viewingNowSocketCatalogueViewInterval = setInterval(() => {
    viewingSince += 1;
    viewCatalogue(viewingSince, { showcaseSlug });

    catalogueViewed(getCompanyId(), getCatalogueId(), _visitor_id, _open_id);
  }, socketInterval);

  _viewing_now_promise = setInterval(() => {
    viewingSince += getFirebaseAnalyticsViewTimeInterval();
    viewCatalogue(viewingSince, { showcaseSlug });

    const more = {
      viewing_since: viewingSince,
      latest_timestamp: getServerTimestamp()
    };

    viewing_now_obj.detail = more;

    if (useQueueForFirebaseAnalytics()) {
      firebaseAnalyticsViewEventMore({
        eventId: viewing_now_id,
        showcaseId,
        visitorId: _visitor_id,
        viewingSince,
        catalogueId: getCatalogueId(),
        openId: _open_id
      }).catch((err) => {
        console.log('ERROR in firebaseAnalyticsViewEventMore', err);
      });

      firebaseAnalyticsOpenEventViewTime({
        openId: _open_id,
        showcaseId,
        visitorId: visitor.id,
        viewingSince,
        domainTag: getDomainTag(),
        catalogueId: getCatalogueId(),
        referrerType: getReferrer() || null,
        referrerValue: getReferrerVal() || null
      }).catch((err) => {
        console.log('ERROR in firebaseAnalyticsOpenEventViewTime', err);
      });
    } else {
      setShowcaseVisitorEventMore(viewing_now_obj, more, { showcaseId });
    }
  }, getFirebaseAnalyticsViewTimeInterval() * 1000);

  if (viewingSince <= 1) {
    if (useQueueForFirebaseAnalytics()) {
      firebaseAnalyticsViewEvent({
        eventId: viewing_now_id,
        showcaseId,
        catalogueId: getCatalogueId(),
        visitorId: visitor.id,
        openId: _open_id,
        viewingSince
      }).catch((err) => {
        console.log('ERROR in firebaseAnalyticsViewEvent', err);
      });
    } else {
      setShowcaseVisitorEvent(viewing_now_obj);
    }
  }
};

export const onFocusEvent = () => {
  try {
    const showcaseId = getShowcaseId();
    const showcaseSlug = getShowcaseSlug();

    if (!_inFocus) {
      windowfocus({ showcaseId, showcaseSlug });
      return;
    }

    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
      scrollTimeout = null;
    }

    scrollTimeout = setTimeout(() => {
      windowblur();
    }, BLUR_TIMEOUT);
  } catch (err) {
    // Handle error
  }
};

const windowfocus = ({ showcaseId, showcaseSlug }) => {
  if (!isAnalyticsEnabled()) {
    return;
  }

  _inFocus = true;

  const o = viewing_now_ref_object;
  if (o && o.status === 'paused') {
    viewingNowProductEvent(o.productId, o.imageId);
  }
  if (_visitor && _open_id) {
    viewingSince += 1;
    viewingNowEvent(_visitor, { showcaseId, showcaseSlug });
  }

  onFocusEvent();
};

export const startFocusTimeout = () => {
  try {
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
      scrollTimeout = null;
    }

    scrollTimeout = setTimeout(() => {
      windowblur();
    }, BLUR_TIMEOUT);
  } catch (err) {
    // Handle error
  }
};

export const tagClickEvent = (tag) => {
  try {
    if (!isAnalyticsEnabled()) {
      return;
    }

    createOrGetVisitor()
      .then(() => {
        const eventRef = getEventRef();
        const eventId = eventRef.key;
        const event = getBasicEventObject(eventId, 'tag_click', null);

        event.detail = { tag };

        openTag({ tag, eventId });

        setShowcaseVisitorEvent(event);
      })
      .catch(() => {});
  } catch (err) {
    // Handle error
  }
};

export const productSelectedEvent = (product_id, order_id, inquiry_id) => {
  try {
    if (!isAnalyticsEnabled()) {
      return;
    }

    const eventRef = getEventRef();
    const obj = getBasicEventObject(eventRef.key, 'product_selected', null);

    obj.detail = {
      product_id,
      order_id,
      inquiry_id
    };

    if (useQueueForFirebaseAnalytics()) {
      firebaseAnalyticsProductSelectedEvent({
        eventId: eventRef.key,
        showcaseId: getShowcaseId(),
        catalogueId: getCatalogueId(),
        visitorId: _visitor_id,
        openId: _open_id || null,
        productId: product_id,
        orderId: order_id,
        inquiryId: inquiry_id
      }).catch((err) => {
        console.log('ERROR in firebaseAnalyticsProductSelectedEvent', err);
      });
    } else {
      setShowcaseVisitorEvent(obj);
    }
  } catch (err) {
    // Handle error
  }
};

export const productUnselectedEvent = (
  product_id,
  order_id,
  inquiry_id,
  { showcaseId, catalogueId }
) => {
  try {
    if (!isAnalyticsEnabled()) {
      return;
    }

    const eventRef = getEventRef({ showcaseId });
    const obj = getBasicEventObject(eventRef.key, 'product_unselected', null, {
      showcaseId,
      catalogueId
    });

    obj.detail = {
      product_id,
      order_id,
      inquiry_id
    };

    if (useQueueForFirebaseAnalytics()) {
      firebaseAnalyticsProductUnselectedEvent({
        eventId: eventRef.key,
        showcaseId,
        catalogueId,
        visitorId: _visitor_id,
        openId: _open_id || null,
        productId: product_id,
        orderId: order_id,
        inquiryId: inquiry_id
      }).catch((err) => {
        console.log('ERROR in firebaseAnalyticsProductUnselectedEvent', err);
      });
    } else {
      setShowcaseVisitorEvent(obj);
    }
  } catch (err) {
    // Handle error
  }
};

const attachFocusListeners = ({ showcaseId, showcaseSlug }) => {
  if (isWindowInFocus()) {
    windowfocus({ showcaseId, showcaseSlug });
  } else {
    windowblur();
  }

  if (focusListenerInitialized) {
    return;
  }

  focusListenerInitialized = true;

  const onFocus = () => {
    if (!initialized) {
      return;
    }

    windowfocus({ showcaseId, showcaseSlug });
  };

  const onBlur = () => {
    if (!initialized) {
      return;
    }

    windowblur();
  };

  EventBus.addListener(EventBus.TYPE.WINDOW_FOCUSED, onFocus);
  EventBus.addListener(EventBus.TYPE.WINDOW_BLURRED, onBlur);

  return () => {
    EventBus.removeListener(EventBus.TYPE.WINDOW_FOCUSED, onFocus);
    EventBus.removeListener(EventBus.TYPE.WINDOW_BLURRED, onBlur);
    focusListenerInitialized = false;
  };
};

export const initialize = () => {
  try {
    if (!isAnalyticsEnabled()) {
      return;
    }

    const showcaseSlug = getShowcaseSlug();

    if (currentAnalyticsSlug && currentAnalyticsSlug !== showcaseSlug) {
      cancel()
        .then(() => {
          initialize();
        })
        .catch(() => {});
      return;
    }

    if (initialized) {
      return;
    }

    initialized = true;
    currentAnalyticsSlug = showcaseSlug;

    const showcaseId = getShowcaseId();
    const catalogueId = getCatalogueId();

    createOrGetVisitor()
      .then((visitor) => {
        _visitor_id = visitor.id;
        _visitor = visitor;

        openEvent(visitor, { showcaseSlug });

        connectedRef = getSecondaryDatabaseRef('.info/connected');
        primaryConnectedRef = getDatabaseRef('.info/connected');

        onDatabaseValue(connectedRef, (snap) => {
          if (snap.val() === true) {
            onlineShowcaseVisitorConnection = onDatabaseDisconnect(
              getSecondaryDatabaseRef(
                `online-showcase-visitors/${showcaseId}/${visitor.id}/${_open_id}`
              )
            );
            onlineShowcaseVisitorConnection.remove();
            onlineCatalogueVisitorConnection = onDatabaseDisconnect(
              getSecondaryDatabaseRef(
                `online-catalogue-visitors/${catalogueId}/${visitor.id}/${_open_id}`
              )
            );
            onlineCatalogueVisitorConnection.remove();
            setDatabaseRef(onlineShowcaseVisitorConnection, true).catch(() => {});
            setDatabaseRef(onlineCatalogueVisitorConnection, true).catch(() => {});
          }
        });

        onDatabaseValue(primaryConnectedRef, (snap) => {
          if (snap.val() === true) {
            primaryOnlineShowcaseVisitorConnection = onDatabaseDisconnect(
              getDatabaseRef(`online-showcase-visitors/${showcaseId}/${visitor.id}/${_open_id}`)
            );
            primaryOnlineShowcaseVisitorConnection.remove();
            primaryOnlineCatalogueVisitorConnection = onDatabaseDisconnect(
              getDatabaseRef(`online-catalogue-visitors/${catalogueId}/${visitor.id}/${_open_id}`)
            );
            primaryOnlineCatalogueVisitorConnection.remove();
            setDatabaseRef(primaryOnlineShowcaseVisitorConnection, true).catch(() => {});
            setDatabaseRef(primaryOnlineCatalogueVisitorConnection, true).catch(() => {});
          }
        });

        focusListenersSubscriber = attachFocusListeners({ showcaseId, showcaseSlug });
      })
      .catch(() => {});
  } catch (err) {
    // Handle error
  }
};

export const cancel = () => {
  try {
    cancelPreviousProductViewingNowInterval('finished');

    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
      scrollTimeout = null;
    }

    viewing_now_ref_object = {};

    if (_viewing_now_product_promise) {
      clearInterval(_viewing_now_product_promise);
      _viewing_now_product_promise = null;
    }

    if (_viewingNowSocketProductViewInterval) {
      clearInterval(_viewingNowSocketProductViewInterval);
      _viewingNowSocketProductViewInterval = null;
    }

    if (_viewing_now_promise) {
      clearInterval(_viewing_now_promise);
      _viewing_now_promise = null;
    }

    if (_viewingNowSocketCatalogueViewInterval) {
      clearInterval(_viewingNowSocketCatalogueViewInterval);
      _viewingNowSocketCatalogueViewInterval = null;
    }

    _inFocus = false;
    _open_id = null;
    viewingSince = 0;
    viewing_now_event_ref = null;
    pausedInterval = false;

    if (viewingNowProductEventTimerRef) {
      clearTimeout(viewingNowProductEventTimerRef);
      viewingNowProductEventTimerRef = null;
    }

    isTimeoutInProgress = false;

    const promises = [];

    if (onlineShowcaseVisitorConnection) {
      promises.push(onlineShowcaseVisitorConnection.remove());
    }

    if (onlineCatalogueVisitorConnection) {
      promises.push(onlineCatalogueVisitorConnection.remove());
    }

    if (connectedRef) {
      promises.push(offDatabase(connectedRef, 'value'));
    }

    if (primaryOnlineShowcaseVisitorConnection) {
      promises.push(primaryOnlineShowcaseVisitorConnection.remove());
    }

    if (primaryOnlineCatalogueVisitorConnection) {
      promises.push(primaryOnlineCatalogueVisitorConnection.remove());
    }

    if (primaryConnectedRef) {
      promises.push(offDatabase(primaryConnectedRef, 'value'));
    }

    onlineShowcaseVisitorConnection = null;
    onlineCatalogueVisitorConnection = null;
    primaryOnlineShowcaseVisitorConnection = null;
    primaryOnlineCatalogueVisitorConnection = null;

    cancelApiAnalytics();

    if (focusListenersSubscriber) {
      focusListenersSubscriber();
    }

    initialized = false;
    currentAnalyticsSlug = null;

    return new Promise((resolve) => {
      Promise.all(promises)
        .then(() => resolve())
        .catch(() => {
          resolve();
        });
    });
  } catch (err) {
    // Handle error
  }
};
